/**
 * Whether she is allowed to make a sound.
 *
 * On by default, and mixed quiet enough that "on by default" is defensible:
 * the whole point of a signature sound is being recognised, and a setting
 * nobody finds is a setting nobody turns on. Shipping silent would mean
 * shipping a feature that, in practice, does not exist.
 *
 * The gate is the setting alone, deliberately not the voice register. Dry mode
 * is her with the volume down on *words* — the same her, muzzled — and muting
 * her sounds along with her sentences would turn a tone control into a persona
 * switch, which is not what the label promises.
 *
 * Read synchronously from a mirrored value for the same reason the theme is:
 * a cue fires from inside an event handler and cannot await Dexie to find out
 * whether it is allowed to.
 */

import { create } from 'zustand'
import { getMeta, setMeta } from '../db/dexie.js'
import { stopBed } from './engine.js'

const META_KEY = 'sound.enabled'

interface SoundState {
  enabled: boolean
}

export const useSoundSetting = create<SoundState>(() => ({ enabled: true }))

export function soundEnabled(): boolean {
  return useSoundSetting.getState().enabled
}

export function setSoundEnabled(enabled: boolean): void {
  useSoundSetting.setState({ enabled })
  // A bed already looping does not survive the switch being turned off; every
  // other cue is short enough to be allowed to finish.
  if (!enabled) stopBed()
  void setMeta(META_KEY, enabled)
}

/** Reconcile with the durable store once, at startup. */
export async function loadSoundSetting(): Promise<void> {
  const stored = await getMeta<unknown>(META_KEY, null)
  if (typeof stored === 'boolean') useSoundSetting.setState({ enabled: stored })
}
